import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { EntityType, Role } from '@gen-common/enums';

import { PermissionService } from '../features/setting/permission.service';

import { AuthService } from './auth.service';

import { PermissionKey } from '@/shared/models/permission.structure';

@Injectable({ providedIn: 'root' })
export class PermissionGuard implements CanActivate {
  private router: Router = inject(Router);
  private authService: AuthService = inject(AuthService);
  private permissionService: PermissionService = inject(PermissionService);

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const entityType: EntityType = route.data['entity'];
    const blockName: string | undefined = route.data['blockName'];

    console.debug('[PermissionGuard] Checking route access.', {
      url: state.url,
      routePath: route.routeConfig?.path,
      entityType,
      blockName,
      auth: this.authService.getDebugSnapshot(),
      permissions: this.permissionService.getDebugSnapshot()
    });

    const sessionExists = await this.authService.ensureSession();
    if (!sessionExists) {
      return false;
    }

    const permissionsReady = await this.permissionService.ensurePermissionsLoaded();
    if (this.authService.vpnRequired) {
      return false;
    }

    if (!permissionsReady) {
      console.error('[PermissionGuard] Permissions could not be loaded after session validation. Redirecting to home.', {
        url: state.url,
        routePath: route.routeConfig?.path,
        entityType,
        auth: this.authService.getDebugSnapshot(),
        permissions: this.permissionService.getDebugSnapshot()
      });
      return this.router.parseUrl('/');
    }

    const entityMobileRedirect = this.getEntityMobileRedirect(entityType, state.url);
    if (entityMobileRedirect) {
      console.info('[PermissionGuard] Redirecting mobile-only role from desktop route to mobile route.', {
        from: state.url,
        to: entityMobileRedirect.toString(),
        auth: this.authService.getDebugSnapshot()
      });
      return entityMobileRedirect;
    }

    const hasPermit = this.permissionService.hasPermission(entityType, [PermissionKey.VIEW, PermissionKey.EDIT], !blockName, blockName);

    if (!hasPermit) {
      console.warn('[PermissionGuard] Permission denied. Redirecting to unauthorized.', {
        url: state.url,
        routePath: route.routeConfig?.path,
        entityType,
        blockName,
        auth: this.authService.getDebugSnapshot(),
        permissions: this.permissionService.getDebugSnapshot()
      });
      return this.router.createUrlTree(['/unauthorized'], {
        queryParams: {
          from: state.url,
          reason: 'permission'
        }
      });
    }

    return true;
  }

  private getEntityMobileRedirect(entityType: EntityType, url: string): UrlTree | null {
    const roles = this.authService.roles;
    const isCleanerOnly = roles.length === 1 && roles.includes(Role.CLEANER);
    if (isCleanerOnly && entityType === EntityType.CLEANING && this.isDesktopCleaningUrl(url)) {
      return this.router.parseUrl(url.replace(/^\/cleaning(?=\/|\?|$)/, '/cleaning-mobile'));
    }

    const isTechnicianOnly = roles.length === 1 && roles.includes(Role.TECHNICIAN);
    if (isTechnicianOnly && entityType === EntityType.TASK && this.isDesktopTaskUrl(url)) {
      return this.router.parseUrl(url.replace(/^\/task(?=\/|\?|$)/, '/task-mobile'));
    }

    return null;
  }

  private isDesktopCleaningUrl(url: string): boolean {
    return /^\/cleaning(?=\/|\?|$)/.test(url);
  }

  private isDesktopTaskUrl(url: string): boolean {
    return /^\/task(?=\/|\?|$)/.test(url);
  }
}
